import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, tokenStore, ApiError, onAuthExpired, resetAuthCircuit, isJwtExpired } from "@/lib/api";
import { SessionExpiredDialog } from "@/components/SessionExpiredDialog";

export type ModuleKey = "dashboard" | "members" | "treasury" | "mailing" | "admin" | "documents";
export type PermissionLevel = "none" | "read" | "write" | "admin";

const LEVELS: Record<PermissionLevel, number> = { none: 0, read: 1, write: 2, admin: 3 };

interface Profile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_super_admin: boolean;
}

interface LoginResponse {
  token: string;
  user: Profile;
}

interface PermissionsResponse {
  is_super_admin: boolean;
  permissions: Record<ModuleKey, PermissionLevel>;
}

function normalizePermissionsResponse(
  payload: PermissionsResponse | Partial<Record<ModuleKey, PermissionLevel>> | null | undefined,
): PermissionsResponse {
  const empty: PermissionsResponse = {
    is_super_admin: false,
    permissions: defaultPerms,
  };

  if (!payload || typeof payload !== "object") return empty;

  if ("permissions" in payload) {
    return {
      is_super_admin: Boolean(payload.is_super_admin),
      permissions: { ...defaultPerms, ...(payload.permissions ?? {}) },
    };
  }

  return {
    is_super_admin: false,
    permissions: { ...defaultPerms, ...payload },
  };
}

interface AuthContextValue {
  user: Profile | null;
  profile: Profile | null;
  permissions: Record<ModuleKey, PermissionLevel>;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  hasAccess: (module: ModuleKey, min?: PermissionLevel) => boolean;
  refresh: () => Promise<void>;
}

const defaultPerms: Record<ModuleKey, PermissionLevel> = {
  dashboard: "none",
  members: "none",
  treasury: "none",
  mailing: "none",
  admin: "none",
  documents: "none",
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [permissions, setPermissions] = useState<Record<ModuleKey, PermissionLevel>>(defaultPerms);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState<null | "expired" | "unauthorized">(null);

  const loadPermissions = useCallback(async () => {
    try {
      const raw = await api.get<PermissionsResponse | Partial<Record<ModuleKey, PermissionLevel>>>("/api/auth/permissions", { rawJson: true });
      console.log("[auth] /auth/permissions raw response:", raw);
      const perms = normalizePermissionsResponse(raw);
      console.log("[auth] normalized permissions:", perms);
      if (perms.is_super_admin) {
        setPermissions({ dashboard: "admin", members: "admin", treasury: "admin", mailing: "admin", admin: "admin", documents: "admin" });
      } else {
        setPermissions(perms.permissions);
      }
    } catch (e) {
      console.error("[auth] loadPermissions failed:", e);
      setPermissions(defaultPerms);
    }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const me = await api.get<Profile>("/api/auth/me", { rawJson: true });
      console.log("[auth] /auth/me raw response:", me);
      const user = (me as unknown as { user?: Profile })?.user ?? me;
      setProfile(user as Profile);
      await loadPermissions();
    } catch (e) {
      console.error("[auth] loadMe failed:", e);
      if (e instanceof ApiError && e.status === 401) {
        tokenStore.clear();
      }
      setProfile(null);
      setPermissions(defaultPerms);
    }
  }, [loadPermissions]);

  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setLoading(false);
      return;
    }
    // Vérification locale : si le token est déjà expiré, on évite TOUT appel
    // réseau (pour ne pas se faire blacklister par LWS sur des 401 répétés).
    if (isJwtExpired(token)) {
      tokenStore.clear();
      setProfile(null);
      setPermissions(defaultPerms);
      setSessionExpired("expired");
      setLoading(false);
      return;
    }
    loadMe().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Écoute centralisée des expirations détectées par la couche API
  // (token expiré localement ou 401 renvoyé par le serveur).
  useEffect(() => {
    return onAuthExpired((reason) => {
      setProfile(null);
      setPermissions(defaultPerms);
      setSessionExpired(reason);
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      // Réarmer le circuit-breaker avant toute tentative de connexion.
      resetAuthCircuit();
      setSessionExpired(null);
      const res = await api.post<LoginResponse>(
        "/api/auth/login",
        { email, password },
        { noAuth: true, rawJson: true }
      );
      if (!res?.token || !res?.user) {
        return { error: "Réponse invalide du serveur" };
      }
      tokenStore.set(res.token);
      setProfile(res.user);
      await loadPermissions();
      return { error: null };
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Erreur de connexion";
      return { error: msg };
    }
  }, [loadPermissions]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", undefined, { rawJson: true });
    } catch {
      // best-effort
    }
    tokenStore.clear();
    setProfile(null);
    setPermissions(defaultPerms);
  }, []);

  const refresh = useCallback(async () => {
    if (tokenStore.get()) await loadMe();
  }, [loadMe]);

  const hasAccess = useCallback((module: ModuleKey, min: PermissionLevel = "read") => {
    if (profile?.is_super_admin) return true;
    return LEVELS[permissions[module]] >= LEVELS[min];
  }, [profile, permissions]);

  const value = useMemo(
    () => ({
      user: profile,
      profile,
      permissions,
      loading,
      signIn,
      signOut,
      hasAccess,
      refresh,
    }),
    [profile, permissions, loading, signIn, signOut, hasAccess, refresh],
  );

  const handleReconnect = useCallback(() => {
    setSessionExpired(null);
    tokenStore.clear();
    resetAuthCircuit();
    setProfile(null);
    setPermissions(defaultPerms);
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }, []);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredDialog open={sessionExpired !== null} reason={sessionExpired} onConfirm={handleReconnect} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
